import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  FileText,
  Home,
  BookOpen,
  BarChart3,
  Building2,
  Upload,
  History,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./MultiAMCLayout.css";

function MultiAMCLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [apiDocsExpanded, setApiDocsExpanded] = useState(false);
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("/api/extraction/multi-amc/statistics", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.statistics) {
            setStats(data.statistics);
          }
        }
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };
    fetchStats();
  }, []);

  // Get API Docs URL
  const getApiDocsUrl = () => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    return `${baseUrl}/api/extraction/multi-amc/docs`;
  };

  const menuItems = [
    { path: "/multi-amc/dashboard", icon: Home, label: "Dashboard", exact: true },
    { path: "/multi-amc/extract", icon: Upload, label: "Upload & Extract", color: "#3b82f6", description: "New Extraction" },
    { path: "/multi-amc/work-orders", icon: History, label: "Work Orders", color: "#8b5cf6", description: "Extraction History" },
    { path: "/multi-amc/amcs", icon: Building2, label: "AMC List", color: "#10b981", description: "Supported AMCs" },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path || location.pathname === "/multi-amc" || location.pathname === "/multi-amc/";
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/multi-amc" || location.pathname === "/multi-amc/" || location.pathname === "/multi-amc/dashboard") {
      return "Multi-AMC Dashboard";
    }
    if (location.pathname.includes("/work-orders/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.description ? `${item.label} - ${item.description}` : item.label;
    }
    return "Multi-AMC Extraction";
  };

  return (
    <div className="mamc-layout">
      {/* Sidebar */}
      <aside className="mamc-sidebar">
        <div className="mamc-sidebar-header">
          {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
            <button 
              onClick={() => navigate("/dashboard")} 
              className="mamc-back-btn"
              title="Back to Main Dashboard"
            >
              <span>←</span> Main Dashboard
            </button>
          )}
          <div className="mamc-logo">
            <Building2 size={28} className="mamc-logo-icon" />
            <span className="mamc-logo-text">Multi-AMC</span>
          </div>
          <div className="mamc-logo-subtitle">MF Form Extraction</div>
        </div>

        <nav className="mamc-sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`mamc-nav-item ${isActive(item) ? "active" : ""}`}
              onClick={() => navigate(item.path)}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="mamc-nav-item-content">
                <span className="mamc-nav-label">{item.label}</span>
                {item.description && <span className="mamc-nav-description">{item.description}</span>}
              </div>
            </button>
          ))}

          {/* API Docs */}
          <button
            className="mamc-nav-item mamc-nav-item-external"
            onClick={() => window.open(getApiDocsUrl(), "_blank")}
            title="API Docs"
          >
            <BookOpen size={20} />
            <div className="mamc-nav-item-content">
              <span className="mamc-nav-label">API Docs</span>
              <span className="mamc-nav-description">Swagger UI</span>
            </div>
            <ExternalLink size={14} className="mamc-external-icon" />
          </button>
        </nav>

        <div className="mamc-sidebar-footer">
          {/* Stats Summary */}
          <div className="mamc-sidebar-stats">
            <div className="mamc-stats-header">
              <BarChart3 size={16} />
              <span className="mamc-stats-title">Statistics</span>
            </div>
            <div className="mamc-stats-items">
              <div className="mamc-stats-item">
                <span className="mamc-stats-label">Total</span>
                <span className="mamc-stats-value">{stats.total}</span>
              </div>
              <div className="mamc-stats-item">
                <span className="mamc-stats-label" style={{ color: '#22c55e' }}>Completed</span>
                <span className="mamc-stats-value">{stats.completed}</span>
              </div>
              <div className="mamc-stats-item">
                <span className="mamc-stats-label" style={{ color: '#f59e0b' }}>Processing</span>
                <span className="mamc-stats-value">{stats.processing}</span>
              </div>
              <div className="mamc-stats-item">
                <span className="mamc-stats-label" style={{ color: '#ef4444' }}>Failed</span>
                <span className="mamc-stats-value">{stats.failed}</span>
              </div>
            </div>
          </div>

          <button
            className="mamc-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="mamc-nav-item-content">
              <span className="mamc-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="mamc-layout-content">
        {/* Top Header */}
        <header className="mamc-top-header">
          <div className="mamc-header-title">{getPageTitle()}</div>

          <div className="mamc-header-actions">
            <NotificationsPanel />
            
            <div className="mamc-user-menu">
              <button
                className="mamc-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="mamc-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || "M"}
                </div>
                <span className="mamc-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="mamc-user-dropdown">
                  <div className="mamc-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="mamc-dropdown-name">{user?.name || "Multi-AMC User"}</div>
                      <div className="mamc-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="mamc-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="mamc-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default MultiAMCLayout;
